import {
  DateTime,
  formatDate as formatDateLuxon,
  formatTime as formatTimeLuxon,
  now,
  parseToDateTime,
  parseWithFormat,
} from '@calendly/datetime'
import { I18n } from '@calendly/i18n'
import moment from 'moment'

import { intlLocaleFor, momentFormatLocaleFor } from 'services/locale_mapping'
import { strftimeToMomentFormat } from 'services/strftime_to_moment_format'

const isLuxonDateTime = (v) => (
  v != null && typeof v.toFormat === 'function' && typeof v.isValid === 'boolean' && v.isValid
)

/** Normalize Moment, Luxon DateTime, ISO string, Date, or ms to Luxon for shared formatting logic. */
const asLuxonDateTime = (v) => {
  if (v == null) return null
  if (isLuxonDateTime(v)) return v
  if (moment.isMoment(v)) {
    if (!v.isValid()) return null
    // Preserve Moment's zone (or UTC offset) so wall-clock format matches Moment.format
    const zone = typeof v.tz === 'function' ? v.tz() : null
    if (zone) {
      return DateTime.fromMillis(v.valueOf(), { zone })
    }
    const offsetMinutes = v.utcOffset()
    const sign = offsetMinutes >= 0 ? '+' : '-'
    const abs = Math.abs(offsetMinutes)
    const hh = String(Math.floor(abs / 60)).padStart(2, '0')
    const mm = String(abs % 60).padStart(2, '0')
    return DateTime.fromMillis(v.valueOf(), { zone: `UTC${sign}${hh}:${mm}` })
  }
  const dt = parseToDateTime(v)
  return dt?.isValid ? dt : null
}

/**
 * Calendar month bucket key (first day of month as YYYY-MM-DD) for Redux maps keyed by month.
 * booking-kit may pass Moment, Luxon, or ISO-like values for a selected day’s `date`.
 */
export const monthStartKeyFromUnknownDate = (v) => {
  const dt = asLuxonDateTime(v)
  return dt?.isValid ? dt.startOf('month').toFormat('yyyy-MM-dd') : null
}

/**
 * Single-day id as YYYY-MM-DD for booking-kit `activeDayIds` and matching API `days[].date` rows.
 * API or parsers may supply strings, Moment, or Luxon; booking-kit expects string ids for cells.
 */
export const dayIdFromUnknownDate = (v) => {
  const dt = asLuxonDateTime(v)
  if (dt?.isValid) return dt.toFormat('yyyy-MM-dd')
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10)
  return null
}

/** Parse a wall-clock time string → Luxon DateTime, or null if invalid. */
export const parseTime = (text, notation = '24h') => (
  parseWithFormat(text, notation === '24h' ? 'HH:mm' : 'hh:mma')
)

/**
 * Format a time for display. Positional API preserved for legacy callers.
 * Accepts Moment, Luxon DateTime, ISO string, Date, or ms (via asLuxonDateTime).
 */
export const formatTime = (value, notation = '12h', format12hr) => {
  const dt = asLuxonDateTime(value)
  if (!dt) return ''

  return formatTimeLuxon(dt, {
    notation,
    ...(notation === '12h' && format12hr ? { format: format12hr } : {}),
  })
}

export const formatDate = (dateMoment, strftimeFormat, locale = I18n.locale) => {
  if (isLuxonDateTime(dateMoment)) {
    return formatDateLuxon(dateMoment, {
      strftime: strftimeFormat,
      locale: intlLocaleFor(locale),
    })
  }
  const momentFormat = strftimeToMomentFormat(strftimeFormat)
  return dateMoment.clone().locale(momentFormatLocaleFor(locale)).format(momentFormat)
}

/**
 * Map API date notation values to locale format keys.
 * Locale files use "american" and "european_standard"; API may return "mdy", "MM/DD/YYYY", etc.
 */
const normalizeDateNotation = (userDateNotation) => {
  const n = (userDateNotation || 'american').toLowerCase()
  if (n === 'mdy' || n === 'mm/dd/yyyy') return 'american'
  if (n === 'dmy' || n === 'dd/mm/yyyy') return 'european_standard'
  if (n === 'american' || n === 'european_standard') return n
  return 'american'
}

export const userDateFormat = (
  dateMoment,
  userDateNotation = 'american',
  format = 'date_large',
  locale = I18n.locale
) => {
  const notation = normalizeDateNotation(userDateNotation)
  format = I18n.t(`time.formats.${notation}.${format}`, { locale })
  return formatDate(dateMoment, format, locale)
}

export const userDateFormatCheckYear = (
  dateMoment,
  userDateNotation = 'american',
  withoutYearformat = 'date_extra',
  withYearFormat = 'date_full',
  locale = I18n.locale
) => {
  const sameCalendarYear = isLuxonDateTime(dateMoment)
    ? dateMoment.year === now().year
    : dateMoment.year() === moment().year()
  const format = sameCalendarYear ? withoutYearformat : withYearFormat
  return userDateFormat(dateMoment, userDateNotation, format, locale)
}

export const userDateTimeFormat = (
  dateMoment,
  userDateNotation = 'american',
  userTimeNotation = '12h',
  format = 'date_time_full',
  locale = I18n.locale
) => {
  const notation = normalizeDateNotation(userDateNotation)
  format = I18n.t(`time.formats.${notation}.${userTimeNotation}.${format}`, { locale })
  return formatDate(dateMoment, format, locale)
}

export const userDateRangeFormat = (
  fromMoment,
  toMoment,
  dateNotation,
  withoutYearFormat,
  withYearFormat
) => {
  const fromLux = asLuxonDateTime(fromMoment)
  const toLux = asLuxonDateTime(toMoment)
  const sameYear = fromLux && toLux
    ? fromLux.year === toLux.year
    : fromMoment.year() === toMoment.year()
  const fromFormat = sameYear ? withoutYearFormat : withYearFormat

  const fromStr = userDateFormat(fromMoment, dateNotation, fromFormat)
  const toStr = userDateFormat(toMoment, dateNotation, withYearFormat)

  return [fromStr, toStr]
}

export const shortUserDateRangeFormat = (
  fromMoment,
  toMoment,
  dateNotation = 'american',
  withoutYearFormat = 'date_small',
  withYearFormat = 'date_small_with_year'
) => {
  const fromLux = asLuxonDateTime(fromMoment)
  const toLux = asLuxonDateTime(toMoment)
  const sameYear = fromLux && toLux
    ? fromLux.year === toLux.year
    : fromMoment.year() === toMoment.year()
  const fromFormat = sameYear ? withoutYearFormat : withYearFormat

  const fromStr = userDateFormat(fromMoment, dateNotation, fromFormat)
  let toStr = userDateFormat(toMoment, dateNotation, 'date_small_with_year')

  const sameMonth = fromLux && toLux
    ? fromLux.hasSame(toLux, 'month')
    : fromMoment.isSame(toMoment, 'month')
  if (sameMonth) {
    toStr = toStr.split(' ').filter((datePart, i) => i !== 0).join(' ')
  }

  return [fromStr, toStr]
}

export const shortUserDateRangeFormatWithNoYear = (
  fromMoment,
  toMoment,
  dateNotation = 'american',
) => {
  const fromStr = userDateFormat(fromMoment, dateNotation, 'date_small')
  let toStr = userDateFormat(toMoment, dateNotation, 'date_small')

  const fromLux = asLuxonDateTime(fromMoment)
  const toLux = asLuxonDateTime(toMoment)
  const sameMonth = fromLux && toLux
    ? fromLux.hasSame(toLux, 'month')
    : fromMoment.isSame(toMoment, 'month')
  if (sameMonth) {
    toStr = toStr.split(' ').filter((datePart, i) => i !== 0).join(' ')
  }

  return [fromStr, toStr]
}

export const formatTimeRange = (
  startTimeMoment,
  endTimeMoment,
  userTimeNotation = '12h',
  includeTimezone = false
) => {
  if (isLuxonDateTime(startTimeMoment) && isLuxonDateTime(endTimeMoment)) {
    if (userTimeNotation !== '12h') {
      return `${startTimeMoment.toFormat('HH:mm')} – ${endTimeMoment.toFormat('HH:mm')}`
    }
    let start = startTimeMoment.toFormat('h:mm a').toLowerCase()
    let end = endTimeMoment.toFormat('h:mm a').toLowerCase()
    start = start.replace(':00', '')
    end = end.replace(':00', '')
    let range = `${start} – ${end}`
    if (includeTimezone) {
      range += ` (${startTimeMoment.toFormat('z')})`
    }
    return range
  }

  if (userTimeNotation !== '12h') {
    return `${startTimeMoment.format('HH:mm')} – ${endTimeMoment.format('HH:mm')}`
  }

  let start = startTimeMoment.format('h:mm a')
  let end = endTimeMoment.format('h:mm a')

  // Remove :00 when top of the hour
  start = start.replace(':00', '')
  end = end.replace(':00', '')
  let range = `${start} – ${end}`

  if (includeTimezone) {
    range += ` (${startTimeMoment.format('z')})`
  }

  return range
}
